import React, { useMemo } from 'react';
import { Button, Input } from '@attentive-platform/stem-ui';

import BrandMark from './BrandMark';
import TradeExclusionsCard from './TradeExclusionsCard';
import { isValidEmail } from '../../Utils/HelperFunctions';
import { scopeHasSelectedElements } from './scopeSelection';
import { normalizeTradeExclusions } from './tradeExclusions';
import { ReviewScreenStyles as S } from './styles';

const modifierValue = (modifier, answer) => {
  if (!modifier) return '';
  if (modifier.modifier_type === 'multi_select') {
    return (answer?.selected_options || []).join(', ') || '—';
  }
  if (modifier.modifier_type === 'dropdown') {
    return (answer?.selected_options && answer.selected_options[0]) || '—';
  }
  if (modifier.modifier_type === 'subjective') {
    return answer?.text_value || '—';
  }
  return '';
};

const answeredModifiers = (modifiers, draftModifiers) =>
  (modifiers || [])
    .map(m => ({ modifier: m, answer: draftModifiers[m.id] }))
    .filter(({ modifier, answer }) => {
      if (modifier.modifier_type === 'multi_select') return (answer?.selected_options || []).length > 0;
      if (modifier.modifier_type === 'dropdown') return !!(answer?.selected_options && answer.selected_options[0]);
      if (modifier.modifier_type === 'subjective') return !!answer?.text_value;
      return false;
    });

const submitHintText = ({ totalPicked, onBehalf, submitExtraDisabled, emailValid, isReopened }) => {
  if (totalPicked === 0) return 'Pick at least one item before submitting';
  if (onBehalf && submitExtraDisabled) return 'Add an internal note to submit';
  if (onBehalf) return 'Attributed as an internal fill on this record.';
  if (!emailValid) return 'Enter your email to submit';
  if (isReopened) return 'Your earlier version stays on file.';
  return 'You can ask us to reopen it later if something changes.';
};

const groupSections = trade => {
  const sections = new Map();
  (trade.scopes || []).forEach(sc => {
    const key = sc.section_id || sc.section_name || 'section';
    const name = sc.section_name || 'Items';
    if (!sections.has(key)) sections.set(key, { id: key, name, items: [] });
    sections.get(key).items.push(sc);
  });
  const list = Array.from(sections.values());
  return list.length ? list : [{ id: 'all', name: 'Items', items: trade.scopes || [] }];
};

const ReviewScreen = ({
  trades,
  draft,
  email,
  onEmail,
  onEditTrade,
  onBackToForm,
  onSubmit,
  submitting,
  submitError,
  readOnly,
  isReopened = false,
  aeName,
  onBehalf = false,
  submitExtra = null,
  submitExtraDisabled = false
}) => {
  const { reviewTrades, skippedTradeNames } = useMemo(() => {
    const skipped = [];
    const shown = trades
      .map(t => {
        const sections = groupSections(t)
          .map(sec => ({
            ...sec,
            items: sec.items.filter(sc => scopeHasSelectedElements(sc, draft))
          }))
          .filter(sec => sec.items.length);
        const pickedCount = sections.reduce((a, sec) => a + sec.items.length, 0);
        if (pickedCount === 0) {
          skipped.push(t.trade_name);
          return null;
        }
        const tradeMods = answeredModifiers(t.trade_modifiers, draft.modifiers);
        const note = (draft.notes && draft.notes[t.trade_id]) || '';
        const exclusions = normalizeTradeExclusions(t);
        return { trade: t, sections, pickedCount, tradeMods, note, exclusions };
      })
      .filter(Boolean);
    return { reviewTrades: shown, skippedTradeNames: skipped };
  }, [trades, draft]);

  const totalPicked = reviewTrades.reduce((a, rt) => a + rt.pickedCount, 0);
  const emailValid = onBehalf ? true : !!(email || '').trim() && isValidEmail((email || '').trim());
  const submitDisabled = !emailValid || totalPicked === 0 || submitting || !!submitExtraDisabled;
  const reviewEmpty = reviewTrades.length === 0;
  const reviewSkipped = skippedTradeNames.length > 0 && reviewTrades.length > 0;

  const heading = readOnly
    ? 'Your submitted scope'
    : isReopened
      ? 'Review & resubmit'
      : onBehalf
        ? 'Review & submit for customer'
        : 'Review & submit';
  const title = readOnly ? 'What we’re measuring for you' : 'Here’s what we’ll measure';
  const summaryLabel =
    totalPicked === 1
      ? `1 item across ${reviewTrades.length === 1 ? '1 trade.' : `${reviewTrades.length} trades.`}`
      : `${totalPicked} items across ${reviewTrades.length === 1 ? '1 trade.' : `${reviewTrades.length} trades.`}`;
  const skippedLabel = `Not shown: ${skippedTradeNames.join(', ')} — nothing selected in ${
    skippedTradeNames.length === 1 ? 'that trade' : 'those trades'
  }.`;

  return (
    <S.PageWrap>
      <S.TopBar>
        <S.TopBarInner>
          <S.BrandRow>
            <BrandMark compact />
            <S.Divider />
            <S.Subtitle>{heading}</S.Subtitle>
          </S.BrandRow>
          <Button variant='text' color='primary' size='large' onClick={onBackToForm}>
            Back to form
          </Button>
        </S.TopBarInner>
      </S.TopBar>

      <S.Body>
        <S.IntroBlock>
          <S.Heading>{title}</S.Heading>
          <S.IntroSubtitle>{summaryLabel} Anything wrong? Jump back into the trade and change it.</S.IntroSubtitle>
        </S.IntroBlock>

        {reviewSkipped && <S.SkippedNote>{skippedLabel}</S.SkippedNote>}

        {reviewEmpty && (
          <S.EmptyStateCard>
            <S.EmptyStateTitle>Nothing selected yet</S.EmptyStateTitle>
            <S.EmptyStateBody>Go back into the trades you bid and tick the items you want measured.</S.EmptyStateBody>
          </S.EmptyStateCard>
        )}

        {reviewTrades.map(rt => (
          <S.TradeCard key={rt.trade.trade_id}>
            <S.TradeHead>
              <S.TradeHeadLeft>
                <S.TradeName>{rt.trade.trade_name}</S.TradeName>
                <S.CountLabel>
                  {rt.pickedCount} item{rt.pickedCount === 1 ? '' : 's'}
                </S.CountLabel>
              </S.TradeHeadLeft>
              {!readOnly && (
                <Button variant='text' color='primary' size='medium' onClick={() => onEditTrade(rt.trade.trade_id)}>
                  Edit
                </Button>
              )}
            </S.TradeHead>

            {rt.sections.map(sec => (
              <S.SectionBlock key={sec.id}>
                <S.Uppercase>{sec.name}</S.Uppercase>
                {sec.items.map(sc => {
                  const els = (sc.elements || []).filter(e => draft.elements[e.id]);
                  const scopeMods = answeredModifiers(sc.modifiers, draft.modifiers);
                  return (
                    <S.ItemRow key={sc.scope_id}>
                      <S.ItemLabelCol>
                        <S.ItemLabel>{sc.scope_name}</S.ItemLabel>
                        <S.ItemCounter>
                          {els.length} of {(sc.elements || []).length} elements
                        </S.ItemCounter>
                      </S.ItemLabelCol>
                      <S.ItemValueCol>
                        {(sc.elements || []).length > 0 && (
                          <S.ElementsLine>{els.map(e => e.name).join(' · ')}</S.ElementsLine>
                        )}
                        {scopeMods.map(({ modifier, answer }) => (
                          <S.ModifierLine key={modifier.id}>
                            <S.ModifierKey>{modifier.title}:</S.ModifierKey>
                            <S.ModifierVal>{modifierValue(modifier, answer)}</S.ModifierVal>
                          </S.ModifierLine>
                        ))}
                      </S.ItemValueCol>
                    </S.ItemRow>
                  );
                })}
              </S.SectionBlock>
            ))}

            {(rt.tradeMods.length > 0 || rt.note) && (
              <S.DetailingBlock>
                <S.Uppercase>Detailing &amp; deliverables</S.Uppercase>
                {rt.tradeMods.map(({ modifier, answer }) => (
                  <S.ModifierLine key={modifier.id}>
                    <S.ModifierKey>{modifier.title}:</S.ModifierKey>
                    <S.ModifierVal>{modifierValue(modifier, answer)}</S.ModifierVal>
                  </S.ModifierLine>
                ))}
                {rt.note && (
                  <S.NoteBlock>
                    <S.NoteLabel>Trade note</S.NoteLabel>
                    <S.NoteText>{rt.note}</S.NoteText>
                  </S.NoteBlock>
                )}
              </S.DetailingBlock>
            )}

            {rt.exclusions ? (
              <S.ExclusionsWrap>
                <TradeExclusionsCard exclusions={rt.exclusions} compact />
              </S.ExclusionsWrap>
            ) : null}
          </S.TradeCard>
        ))}

        {!readOnly ? (
          <S.SubmitCard>
            <S.SubmitHead>
              <S.SubmitTitle>
                {onBehalf
                  ? 'Submit on behalf of the customer'
                  : isReopened
                    ? 'Resend your revised scope'
                    : 'Send it back to us'}
              </S.SubmitTitle>
              <S.SubmitBody>
                {onBehalf
                  ? 'This is recorded under your name as an internal fill. The customer is not emailed.'
                  : isReopened
                    ? 'We’ll replace your previous version with this one and let your account team know.'
                    : 'We’ll confirm your email so your account team knows who filled this in.'}
              </S.SubmitBody>
            </S.SubmitHead>
            <S.EmailBlock>
              <S.InputLabel>{onBehalf ? 'Customer email' : 'Your email'}</S.InputLabel>
              <Input
                size='large'
                value={email}
                onChange={e => onEmail(e.target.value)}
                placeholder='you@company.com'
                disabled={onBehalf}
              />
            </S.EmailBlock>
            {submitExtra}
            {submitError && <S.ErrorBanner>{submitError}</S.ErrorBanner>}
            <S.SubmitRow>
              <Button
                variant='contained'
                color='primary'
                size='large'
                disabled={submitDisabled}
                loading={submitting}
                onClick={onSubmit}>
                {onBehalf ? 'Submit for customer' : isReopened ? 'Resubmit scope' : 'Submit scope'}
              </Button>
              <S.SubmitHint>
                {submitHintText({ totalPicked, onBehalf, submitExtraDisabled, emailValid, isReopened })}
              </S.SubmitHint>
            </S.SubmitRow>
          </S.SubmitCard>
        ) : (
          <S.ReadOnlyCard>
            <S.ReadOnlyTitle>Already submitted</S.ReadOnlyTitle>
            <S.ReadOnlyBody>
              Sent to {email}. Ask {aeName || 'your account team'} to reopen the form if something needs to change.
            </S.ReadOnlyBody>
          </S.ReadOnlyCard>
        )}
      </S.Body>
    </S.PageWrap>
  );
};

export default ReviewScreen;
